/**
 * Mock WebSocket Client
 *
 * Emits realistic FillEmitted, PositionUpdated, OrderStatusChanged, and L2Tick
 * events on a timer. Complements the MockBrokerClient to provide the full
 * trading experience without a real backend.
 *
 * When a trade is placed:
 *   1. After 200ms: emit OrderStatusChanged (WORKING)
 *   2. After 500-2000ms: emit FillEmitted (85% fill, 10% stay working, 5% reject)
 *   3. On fill: emit PositionUpdated
 *
 * The MockMarketDataEngine provides L2 tick data via subscribe().
 */

import { type WsEvent, type WsListener, type WsStatusListener, type WsStatus } from './wsClient';
import { getOrCreateEngine, type L2Data } from './mockMarketData';

export interface MockOrderContext {
  clOrdId: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  limitPrice: string;
  accountId: string;
}

/**
 * Callback when a fill is confirmed. Allows MockBrokerClient to update its state.
 */
export type FillCallback = (clOrdId: string, symbol: string, side: 'BUY' | 'SELL', quantity: number, fillPrice: number) => void;

/**
 * MockWsClient — in-memory event emitter that simulates WebSocket behavior.
 *
 * Unlike the real WsClient (which reconnects on close), this stays "connected"
 * and emits events to subscribers. Used in demo mode or tests.
 */
export class MockWsClient {
  private listeners = new Set<WsListener>();
  private statusListeners = new Set<WsStatusListener>();
  private status: WsStatus = 'closed';
  private marketDataSubs: Array<() => void> = [];
  private pendingOrders: Map<string, MockOrderContext> = new Map();
  private onFill?: FillCallback;

  constructor(onFill?: FillCallback) {
    this.onFill = onFill;
    this.subscribeToMarketData();
  }

  /**
   * Subscribe to inbound events (fills, position updates, L2 ticks, order status)
   */
  on(fn: WsListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Subscribe to connection status changes
   */
  onStatus(fn: WsStatusListener): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  /**
   * Connect the mock client. Sets status to 'open' immediately.
   */
  async connect(): Promise<void> {
    this.setStatus('open');
  }

  /**
   * Close the mock client. Sets status to 'closed'.
   */
  close(): void {
    this.setStatus('closed');
    this.unsubscribeFromMarketData();
  }

  /**
   * Track a pending order that was just placed.
   * The mock will emit OrderStatusChanged and FillEmitted at appropriate times.
   */
  trackOrder(clOrdId: string, symbol: string, side: 'BUY' | 'SELL', quantity: number, limitPrice: string, accountId: string): void {
    const ctx: MockOrderContext = { clOrdId, symbol, side, quantity, limitPrice, accountId };
    this.pendingOrders.set(clOrdId, ctx);

    // After 200ms, emit OrderStatusChanged (WORKING)
    setTimeout(() => {
      if (this.pendingOrders.has(clOrdId)) {
        this.emit({
          type: 'OrderStatusChanged',
          payload: {
            clOrdId,
            status: 'WORKING',
            symbol,
            side,
            quantity,
            filledQuantity: 0,
            averagePrice: limitPrice,
            createdAt: new Date().toISOString(),
          },
        });
      }
    }, 200);

    // After 500-2000ms, decide fate:
    // 85% FILLED, 10% stay WORKING, 5% REJECTED
    const delay = 500 + Math.random() * 1500;
    setTimeout(() => {
      if (!this.pendingOrders.has(clOrdId)) return;
      this.pendingOrders.delete(clOrdId);

      const rand = Math.random();
      if (rand < 0.85) {
        // Fill the order
        const fillPrice = Number(limitPrice);
        this.onFill?.(clOrdId, symbol, side, quantity, fillPrice);

        this.emit({
          type: 'FillEmitted',
          payload: {
            clOrdId,
            symbol,
            side,
            quantity,
            averagePrice: limitPrice,
            filledAt: new Date().toISOString(),
            fillRef: `FILL-${Math.random().toString(16).slice(2, 8).toUpperCase()}`,
          },
        });
        // Also emit OrderStatusChanged with FILLED
        this.emit({
          type: 'OrderStatusChanged',
          payload: {
            clOrdId,
            status: 'FILLED',
            symbol,
            side,
            quantity,
            filledQuantity: quantity,
            averagePrice: limitPrice,
            filledAt: new Date().toISOString(),
          },
        });
      } else if (rand < 0.95) {
        // Stay working (no action, order remains WORKING from the first emit)
      } else {
        // Reject
        this.emit({
          type: 'OrderStatusChanged',
          payload: {
            clOrdId,
            status: 'REJECTED',
            symbol,
            side,
            quantity,
            filledQuantity: 0,
            averagePrice: limitPrice,
            rejectionReason: 'Insufficient funds',
            rejectedAt: new Date().toISOString(),
          },
        });
      }
    }, delay);
  }

  /**
   * Emit an event to all subscribers. (Used internally; exposed for tests.)
   */
  emit(event: WsEvent): void {
    this.listeners.forEach((fn) => fn(event));
  }

  /**
   * Set the connection status and notify listeners.
   */
  private setStatus(s: WsStatus): void {
    this.status = s;
    this.statusListeners.forEach((fn) => fn(s));
  }

  /**
   * Subscribe to the market data engine's L2 ticks and emit them as WsEvents.
   */
  private subscribeToMarketData(): void {
    const engine = getOrCreateEngine();
    const symbols = engine.getAllSymbols();

    symbols.forEach((symbol) => {
      const unsub = engine.subscribe(symbol, (l2: L2Data) => {
        if (this.status === 'open') {
          this.emit({
            type: 'L2Tick',
            payload: {
              symbol: l2.symbol,
              bid: l2.bid,
              ask: l2.ask,
              bidSize: l2.bidSize,
              askSize: l2.askSize,
              last: l2.last,
              volume: l2.volume,
              timestamp: l2.timestamp,
            },
          });
        }
      });
      this.marketDataSubs.push(unsub);
    });
  }

  /**
   * Unsubscribe from all market data feeds.
   */
  private unsubscribeFromMarketData(): void {
    this.marketDataSubs.forEach((unsub) => unsub());
    this.marketDataSubs = [];
  }
}
